import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { Button } from '@mui/material';
import { useKeycloak } from '@react-keycloak/ssr';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { FullPageError, FullPageStatus, SessionRecoveryFocusAnchor } from '@/componentsv2/misc/FullPageSessionState';
import { isOrganizationValidated, markOrganizationValidated, unmarkOrganizationValidated } from './accessValidation';
import { asOrganizationId } from './identity';
import { acquireOperationContext, isSessionTransition, type OperationContext } from './operation';
import { isOrgRoutePattern } from './organization';
import { getOrganizationList, peekOrganizationList, subscribeOrganizationList } from './organizationList';
import { getLastVisitedOrganization, setLastVisitedOrganization } from './lastVisitedOrganization';
import { getSessionSubject } from './token';
import { useRouteOrganization } from './OrganizationIdentityContext';
import type { Organization } from '@/types/organization';

type GuardState = 'checking' | 'allowed' | 'error' | 'redirecting';
type MembershipState = 'loading' | 'ready' | 'error';

type OrganizationMembershipContextValue = {
  organization: Organization | null;
  state: MembershipState;
  retrying: boolean;
  retry: () => void;
};

const OrganizationMembershipContext = createContext<OrganizationMembershipContextValue>({
  organization: null,
  state: 'ready',
  retrying: false,
  retry: () => {},
});

export const useOrganizationMembership = (): OrganizationMembershipContextValue => useContext(OrganizationMembershipContext);
// Exported so tests can intercept exactly these timers: the automatic backoff
// is a real setTimeout, and a test asserting on the error state otherwise races
// it under load.
export const RETRY_DELAYS = [1, 2, 4, 8, 16, 30].map((seconds) => seconds * 1000);

export const OrganizationEntryGuard = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter();
  const organization = useRouteOrganization();
  const publishedOrganizations = useSyncExternalStore(subscribeOrganizationList, peekOrganizationList, () => null);
  const { keycloak, initialized } = useKeycloak();
  const { t } = useTranslation();
  const authenticated = keycloak?.authenticated ?? false;
  const subject = getSessionSubject();
  const freshOrganization = organization ? (publishedOrganizations?.find((candidate) => candidate.name === organization) ?? null) : null;
  // The validation effect must not depend on the published array itself: every
  // publish writes a new array identity, so an array dependency re-runs the
  // effect on the very refresh it triggered - on the membership-miss path that
  // closes a forced-refresh/redirect loop. Only membership *transitions* may
  // restart validation.
  const isPublishedMember = freshOrganization !== null;
  const hasPublishedList = publishedOrganizations !== null;
  const [state, setState] = useState<GuardState>(() =>
    organization && (freshOrganization || isOrganizationValidated(subject, organization)) ? 'allowed' : 'checking'
  );
  const [membershipError, setMembershipError] = useState<Error | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const [retrying, setRetrying] = useState(false);
  // Counts consecutive failed validation attempts (manual and automatic
  // backoff alike) so the error Alert re-announces each failure; reset on
  // success. Also latches whether this mount ever showed the error surface,
  // so recovery can move focus onto the mounted content instead of <body>.
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [hadGuardError, setHadGuardError] = useState(false);
  const loginStartedRef = useRef(false);
  const routerRef = useRef(router);
  const keycloakRef = useRef(keycloak);
  // Latches the organization a redirect has already been issued for, so a
  // re-entrant effect run (or a blocked navigation) cannot fan out repeated
  // router.replace calls that cancel each other's in-flight transitions.
  const redirectedForRef = useRef<string | null>(null);
  // Latches the organization already background-revalidated by this mount, so
  // effect re-runs (membership transitions) do not refetch the list they
  // themselves published. Manual retry clears it so a failed authoritative
  // refresh can actually be retried.
  const revalidatedForRef = useRef<string | null>(null);
  // While the mount's own validation or forced refresh is settling, its
  // publication re-runs this effect (isPublishedMember flips) before the
  // settlement handler has applied the result. The re-entrant run must not
  // start another validation pass or warm-branch refresh - either would issue
  // a duplicate authoritative request whose outcome the pending settlement
  // already decides.
  const revalidationInFlightRef = useRef<string | null>(null);
  // Effect cleanup also runs when membership publication changes one of the
  // validation dependencies. That is not a component unmount: the request
  // which published the new list still owns its authoritative settlement.
  // Keep true mount ownership separately so that settlement can cross an
  // effect re-run, while a genuine unmount still suppresses publication.
  const componentMountedRef = useRef(true);

  useEffect(
    () => () => {
      componentMountedRef.current = false;
    },
    []
  );

  const retry = useCallback(() => {
    redirectedForRef.current = null;
    revalidatedForRef.current = null;
    setRetrying(true);
    setRetryNonce((value) => value + 1);
  }, []);

  // Retains the last published membership organization for the validated
  // current organization while the list snapshot is absent (invalidation after
  // an in-app organization save, TTL expiry, or a failed background refresh).
  // Adjust during render rather than in an effect so a publication cannot
  // commit one frame with stale derived membership. The retention is narrow:
  // it never applies once a refreshed list exists (a membership miss must stay
  // authoritative) and it requires the sticky validation flag.
  const [retainedMembership, setRetainedMembership] = useState<Organization | null>(null);
  if (freshOrganization && retainedMembership !== freshOrganization) {
    setRetainedMembership(freshOrganization);
  }
  const retainedOrganization =
    !freshOrganization &&
    !hasPublishedList &&
    organization &&
    retainedMembership?.name === organization &&
    isOrganizationValidated(subject, organization)
      ? retainedMembership
      : null;
  const membershipOrganization = freshOrganization ?? retainedOrganization;

  const membershipValue = useMemo<OrganizationMembershipContextValue>(
    () => ({
      organization: membershipOrganization,
      state: membershipError ? 'error' : membershipOrganization ? 'ready' : 'loading',
      retrying: membershipError ? retrying : false,
      retry,
    }),
    [membershipOrganization, membershipError, retry, retrying]
  );

  useEffect(() => {
    routerRef.current = router;
    keycloakRef.current = keycloak;
  });

  // Adjust during render (same pattern as retainedMembership below): the latch
  // must be set in the same pass that renders the error surface, and an effect
  // calling setState directly is flagged by the React Compiler lint.
  if (state === 'error' && !hadGuardError) {
    setHadGuardError(true);
  }

  useEffect(() => {
    if (authenticated) loginStartedRef.current = false;
  }, [authenticated]);

  useEffect(() => {
    if (!initialized || !organization) return;

    if (!authenticated) {
      if (!loginStartedRef.current) {
        loginStartedRef.current = true;
        void (keycloakRef.current as any)?.login({ redirectUri: window.location.href });
      }
      return;
    }

    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let retryIndex = 0;
    // This effect runs after OrganizationScope's layout effect committed the
    // route organization, so a context acquired here observes its own mount's
    // identity. Settlement handlers check the operation, not `mounted`, where
    // a terminal state from a re-entrant run must be preserved.
    const operation: OperationContext = acquireOperationContext(asOrganizationId(organization));

    const applyAuthoritativeResult = (organizations: Organization[]): void => {
      revalidatedForRef.current = organization;
      const requested = organizations.find((candidate) => candidate.name === organization);
      if (requested) {
        redirectedForRef.current = null;
        markOrganizationValidated(operation.subject, requested.name);
        setLastVisitedOrganization(requested.name, operation.subject);
        setMembershipError(null);
        setRetrying(false);
        setFailedAttempts(0);
        setState('allowed');
        return;
      }

      unmarkOrganizationValidated(operation.subject, organization);
      if (redirectedForRef.current === organization) return;
      redirectedForRef.current = organization;
      const lastVisited = getLastVisitedOrganization(operation.subject);
      const fallback = organizations.find((candidate) => candidate.name === lastVisited) ?? organizations[0];
      setState('redirecting');
      // The settlement handlers check the operation, not `mounted`: a
      // re-entrant effect run for the same organization must not suppress the
      // terminal state of a redirect it did not issue, and setState after a
      // true unmount is a no-op anyway.
      void routerRef.current
        .replace(fallback ? `/${fallback.name}` : '/')
        .then((navigated) => {
          // Next resolves false for a blocked navigation (e.g. the
          // import-preview leave guard) instead of rejecting; nothing else can
          // leave 'redirecting', so a false resolution must not strand the spinner.
          if (!navigated && operation.isScopeCurrent()) {
            redirectedForRef.current = null;
            setState('error');
          }
        })
        .catch((_error: Error & { cancelled?: boolean }) => {
          if (operation.isScopeCurrent()) {
            redirectedForRef.current = null;
            setState('error');
          }
        });
    };

    const validateMembership = async (force: boolean, keepCurrentState = false): Promise<void> => {
      if (!componentMountedRef.current || !operation.isScopeCurrent()) return;
      // Automatic backoff retries and manual Retry both stay in the persistent
      // error state; flipping back to 'checking' would unmount the error Alert
      // and drop keyboard focus from its Retry button.
      if (!keepCurrentState) setState('checking');

      revalidationInFlightRef.current = organization;
      try {
        let organizations = await getOrganizationList({ force });
        if (!componentMountedRef.current || !operation.isScopeCurrent()) return;
        if (!force && !organizations.some((candidate) => candidate.name === organization)) {
          organizations = await getOrganizationList({ force: true });
          if (!componentMountedRef.current || !operation.isScopeCurrent()) return;
        }
        applyAuthoritativeResult(organizations);
      } catch (caught) {
        if (!componentMountedRef.current || !operation.isScopeCurrent()) return;
        if (isSessionTransition(caught)) return;
        setRetrying(false);
        setFailedAttempts((value) => value + 1);
        setMembershipError(caught instanceof Error ? caught : new Error('Unable to load organizations'));
        if (!isOrganizationValidated(operation.subject, organization)) setState('error');
        const delay = RETRY_DELAYS[Math.min(retryIndex, RETRY_DELAYS.length - 1)];
        retryIndex += 1;
        retryTimer = setTimeout(() => {
          void validateMembership(force, true);
        }, delay);
      } finally {
        if (revalidationInFlightRef.current === organization) revalidationInFlightRef.current = null;
      }
    };

    // Read the store imperatively: the effect deliberately depends only on
    // primitives derived from the published list (see isPublishedMember), so a
    // closure over the array could be stale here.
    const publishedOrganization = peekOrganizationList()?.find((candidate) => candidate.name === organization);
    if (publishedOrganization) {
      markOrganizationValidated(operation.subject, publishedOrganization.name);
      setLastVisitedOrganization(publishedOrganization.name, operation.subject);
      // A warm snapshot admits instantly but is not the admission authority:
      // converge on the server by forcing one refresh per organization per
      // mount, and consume that refresh's settlement directly. A revocation
      // applies here, once, without depending on a second request; a failed
      // refresh keeps the tenant mounted under the documented sticky envelope
      // but must surface the persistent stale-data Alert. A validation this
      // mount already has in flight is itself authoritative: its own list
      // publication re-runs this effect before it settles, and a forced call
      // cannot join that non-forced request, so refreshing here would only
      // issue a duplicate.
      if (revalidatedForRef.current !== organization && revalidationInFlightRef.current !== organization) {
        revalidatedForRef.current = organization;
        revalidationInFlightRef.current = organization;
        void getOrganizationList({ force: true })
          .then((organizations) => {
            if (!operation.isScopeCurrent()) return;
            applyAuthoritativeResult(organizations);
          })
          .catch((caught: unknown) => {
            if (!operation.isScopeCurrent()) return;
            if (isSessionTransition(caught)) return;
            setRetrying(false);
            setFailedAttempts((value) => value + 1);
            setMembershipError(caught instanceof Error ? caught : new Error('Unable to load organizations'));
            if (!isOrganizationValidated(operation.subject, organization)) setState('error');
          })
          .finally(() => {
            if (revalidationInFlightRef.current === organization) revalidationInFlightRef.current = null;
          });
      }
    } else if (redirectedForRef.current !== organization && revalidationInFlightRef.current !== organization) {
      // While the redirect latch holds, a re-entrant run (e.g. the published
      // list becoming available) must not start another validation pass: the
      // redirect it would conclude with is already in flight or blocked, and
      // re-fetching would only add load. The same applies while this mount's
      // own forced revalidation is still settling - its publication re-runs
      // this effect before the settlement applies. Manual retry clears the
      // latches.
      void validateMembership(retryNonce > 0, retryNonce > 0 || isOrganizationValidated(operation.subject, organization));
    }

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [initialized, authenticated, organization, isPublishedMember, hasPublishedList, retryNonce]);

  if (!isOrgRoutePattern(router.pathname)) {
    return <OrganizationMembershipContext.Provider value={membershipValue}>{children}</OrganizationMembershipContext.Provider>;
  }
  if (router.isReady && !organization) {
    return (
      <FullPageError
        message={t('This organization address is not valid.')}
        retrying={false}
        actionLabel={t('Go to your organizations')}
        onRetry={() => {
          void router.replace('/');
        }}
      />
    );
  }
  if (state === 'allowed' || freshOrganization) {
    return (
      <OrganizationMembershipContext.Provider value={membershipValue}>
        {hadGuardError && <SessionRecoveryFocusAnchor label={t('Organization access verified')} />}
        {children}
      </OrganizationMembershipContext.Provider>
    );
  }

  if (state === 'error') {
    return (
      <FullPageError
        message={t('We could not verify access to this organization. No organization data has been loaded.')}
        retrying={retrying}
        attempt={failedAttempts}
        onRetry={retry}
        secondaryAction={
          <Button
            color='inherit'
            onClick={() => {
              void router.replace('/');
            }}
          >
            {t('Go to your organizations')}
          </Button>
        }
      />
    );
  }

  return (
    <FullPageStatus
      message={t(
        !initialized ? 'Checking your session…' : state === 'redirecting' || !authenticated ? 'Redirecting…' : 'Verifying organization access…'
      )}
    />
  );
};
